<template>
  <div>
    <b-alert
      variant="danger"
      :show="errorMessage ? true : false"
    >
      <div class="alert-body">
        <p>
          {{ errorMessage }}
        </p>
      </div>
    </b-alert>

    <b-row>
      <b-col md="3">
        <b-form-group label="Search">
          <b-form-input
            v-model="search"
            type="text"
            placeholder="Order ID / Doc No"
            @input="debouncedFiltersChange"
          />
        </b-form-group>
      </b-col>

      <b-col md="2">
        <b-form-group label="Target Type">
          <custom-v-select
            v-model="selectedTargetType"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="targetTypeOptions"
            label="text"
            placeholder="Select Type"
            :reduce="data => data.value"
            @input="filtersChangeHandler"
          />
        </b-form-group>
      </b-col>
      <b-col
        md="2"
        class="d-flex flex-column justify-content-center mb-1"
        style="gap: 6px;"
      >
        <b-form-checkbox
          :checked="margStatus === 'pending'"
          @change="margStatusChangeHandler('pending')"
        >
          Pending in Marg
        </b-form-checkbox>
        <b-form-checkbox
          :checked="margStatus === 'updated'"
          @change="margStatusChangeHandler('updated')"
        >
          Updated in Marg
        </b-form-checkbox>
      </b-col>
      <b-col md="2">
        <b-form-group label="Updated By in Marg">
          <custom-v-select
            v-model="selectedReconciledBy"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="usersList"
            label="fullname"
            placeholder="Select User"
            :disabled="loading"
            :reduce="data => data.id"
            @input="filtersChangeHandler"
          />
        </b-form-group>
      </b-col>

      <b-col md="3">
        <b-form-group label="Paid At Date Range">
          <flat-picker
            v-model="paidAtDateRange"
            placeholder="Select Date Range"
            :config="{ mode: 'range'}"
          />
        </b-form-group>
      </b-col>
      <b-col md="3">
        <b-form-group label="Buyers">
          <custom-v-select
            v-model="selectedBuyers"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="buyerOptions"
            label="text"
            placeholder="Select Buyers"
            multiple
            :disabled="loading"
            :close-on-select="false"
            :reduce="data => data.id"
            :deselect-from-dropdown="true"
            @input="filtersChangeHandler"
          />
        </b-form-group>
      </b-col>

      <b-col
        md="3"
        class="position-relative"
      >
        <div style="display: flex; position: absolute; gap: 10px; top: 0; right: 12px;">
          <b-form-checkbox
            :checked="initiatedByType === 'retailer'"
            @change="initiatedByTypeChangeHandler('retailer')"
          >
            Retailer
          </b-form-checkbox>
          <b-form-checkbox
            :checked="initiatedByType === 'staff'"
            @change="initiatedByTypeChangeHandler('staff')"
          >
            Staff
          </b-form-checkbox>
        </div>
        <b-form-group label="Initiated By">
          <custom-v-select
            v-model="selectedInitiatedBy"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="initiatedByOptions"
            label="fullname"
            placeholder="Select Initiated By"
            multiple
            :disabled="loading"
            :close-on-select="false"
            :reduce="data => data.id"
            :deselect-from-dropdown="true"
            @input="filtersChangeHandler"
          />
        </b-form-group>
      </b-col>
      <b-col md="3">
        <b-form-group label="Amount Range">
          <b-row>
            <b-col cols="6">
              <b-form-input
                v-model.number="amountMin"
                type="number"
                min="0"
                step="1000"
                placeholder="Min Amount"
                @input="debouncedFiltersChange"
              />
            </b-col>
            <b-col cols="6">
              <b-form-input
                v-model.number="amountMax"
                type="number"
                min="0"
                step="1000"
                placeholder="Max Amount"
                @input="debouncedFiltersChange"
              />
            </b-col>
          </b-row>
        </b-form-group>
      </b-col>

      <b-col md="3">
        <b-form-group label="Updated in Marg At">
          <flat-picker
            v-model="reconciledAtDateRange"
            placeholder="Select Date Range"
            :config="{ mode: 'range'}"
          />
        </b-form-group>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BRow, BCol, BFormGroup, BFormInput, BFormCheckbox, BAlert,
} from 'bootstrap-vue'
import axios from 'axios'
import CustomVSelect from '@/components/UI/CustomVSelect.vue'
import FlatPicker from '@/components/UI/FlatPicker.vue'

export default {
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BAlert,
    CustomVSelect,
    FlatPicker,
  },
  data() {
    return {
      loading: false,
      errorMessage: '',
      debounceTimer: null,

      search: '',
      selectedTargetType: null,
      targetTypeOptions: [
        { text: 'Bill', value: 'bill' },
        { text: 'Statement', value: 'statement' },
      ],
      amountMin: null,
      amountMax: null,
      paidAtDateRange: null,

      buyerOptions: [],
      selectedBuyers: [],

      // null | 'retailer' | 'staff'
      initiatedByType: null,
      selectedInitiatedBy: [],
      retailerOptions: [],
      usersList: [],

      // null | 'pending' | 'updated'
      margStatus: null,
      selectedReconciledBy: null,
      reconciledAtDateRange: null,
    }
  },
  computed: {
    initiatedByOptions() {
      if (this.initiatedByType === 'retailer') return this.retailerOptions
      if (this.initiatedByType === 'staff') return this.usersList
      return [...this.retailerOptions, ...this.usersList]
    },
  },
  watch: {
    paidAtDateRange(newValue) {
      this.dateRangeChangeHandler(newValue)
    },
    reconciledAtDateRange(newValue) {
      this.dateRangeChangeHandler(newValue)
    },
  },
  created() {
    this.fetchOptions()
  },
  beforeDestroy() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
  },
  methods: {
    debouncedFiltersChange() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer)
      }
      this.debounceTimer = setTimeout(() => {
        this.filtersChangeHandler()
      }, 500)
    },

    dateRangeChangeHandler(newValue) {
      if (newValue) {
        const [fromDate, toDate] = newValue.split(' to ')
        if (fromDate && toDate) {
          this.filtersChangeHandler()
        }
      } else if (newValue === '') {
        this.filtersChangeHandler()
      }
    },

    margStatusChangeHandler(value) {
      // Clicking the checked one unchecks it; clicking the other switches
      this.margStatus = this.margStatus === value ? null : value
      this.filtersChangeHandler()
    },

    initiatedByTypeChangeHandler(value) {
      // Clicking the checked one unchecks it; clicking the other switches
      this.initiatedByType = this.initiatedByType === value ? null : value

      // Drop selected users that are no longer in the visible options
      const availableIds = this.initiatedByOptions.map(item => item.id)
      this.selectedInitiatedBy = this.selectedInitiatedBy.filter(id => availableIds.includes(id))
      this.filtersChangeHandler()
    },

    async fetchOptions() {
      try {
        this.loading = true

        const { data: buyersRes } = await axios.get('/accounts/buyers_options')
        this.buyerOptions = buyersRes.data

        const { data: retailer } = await axios.get('/delivery/users_list?retailers_only=true')
        this.retailerOptions = retailer
        const retailerIds = this.retailerOptions.map(i => i.id)

        const { data: usersList } = await axios.get('/delivery/users_list')
        this.usersList = usersList.filter(user => !retailerIds.includes(user.id))
      } catch (error) {
        this.errorMessage = error?.response?.data?.detail || 'Failed to fetch filter options!'
      } finally {
        this.loading = false
      }
    },

    filtersChangeHandler() {
      const filtersdata = {}

      if (this.search && this.search.trim()) {
        filtersdata.search = this.search.trim()
      }

      if (this.selectedTargetType) {
        filtersdata.target_type = this.selectedTargetType
      }

      if (this.amountMin !== null && this.amountMin !== '') {
        filtersdata.min_amount = this.amountMin
      }
      if (this.amountMax !== null && this.amountMax !== '') {
        filtersdata.max_amount = this.amountMax
      }

      if (this.paidAtDateRange) {
        const [paidAtFrom, paidAtTo] = this.paidAtDateRange.split(' to ')
        if (paidAtFrom && paidAtTo) {
          filtersdata.paid_at_from = paidAtFrom
          filtersdata.paid_at_to = paidAtTo
        }
      }

      if (this.selectedBuyers && this.selectedBuyers.length > 0) {
        filtersdata.buyer = this.selectedBuyers
      }

      if (this.initiatedByType) {
        filtersdata.initiated_by_type = this.initiatedByType
      }

      if (this.selectedInitiatedBy && this.selectedInitiatedBy.length > 0) {
        filtersdata.initiated_by = this.selectedInitiatedBy
      }

      if (this.margStatus === 'pending') {
        filtersdata.reconciled_in_marg = false
      } else if (this.margStatus === 'updated') {
        filtersdata.reconciled_in_marg = true
      }

      if (this.selectedReconciledBy) {
        filtersdata.reconciled_by = this.selectedReconciledBy
      }

      if (this.reconciledAtDateRange) {
        const [reconciledFrom, reconciledTo] = this.reconciledAtDateRange.split(' to ')
        if (reconciledFrom && reconciledTo) {
          filtersdata.reconciled_at_from = reconciledFrom
          filtersdata.reconciled_at_to = reconciledTo
        }
      }

      this.$emit('filtersUpdated', filtersdata)
    },
  },
}
</script>
